import { createReducer, on } from '@ngrx/store';

import {
    ICallableParams,
    ICategories,
    IDataCancelaPedidos,
    IDataCurrentOrders,
    IDataEcommerceConfig,
    IGroup,
    IGroupers,
    IProductos,
    IPromosEcommerce,
    IRecommender,
    IRespOrder,
    IRespSendWebhook,
    ISummaryPrice,
    PedidoActualEntity
} from '../../private/shared/models/productos';
import { DEFAULT_RECOMMENDED, SummaryPriceDefault } from '../../shared/models/Constants';
import {
    activePromosEcommerce,
    addCartPromoState,
    addCartState,
    addingProductsToCartState,
    addingProductsToResumeState,
    addingPromosToCartState,
    addProductCartState,
    addProductsCartState,
    clearCartProducts,
    clearDetailOrder,
    detailOrder,
    editPromo,
    errorCancelaPedidos,
    errorDetailOrder,
    errorEcommerceConfig,
    errorGetProducts,
    errorObtienePedidos,
    errorPromosEcommerce,
    errorSendCart,
    errorSendOrder,
    errorSendWebhook,
    getProducts,
    getPromosEcommerce,
    removeCartPromoState,
    removeCartState,
    resetCancelaPedidos,
    resetEcommerceConfig,
    resetEditPromo,
    resetObtienePedidos,
    resetProducts,
    resetPromosEcommerce,
    resetSendCart,
    resetSendOrder,
    resetSendWebhook,
    sendCancelaPedidos,
    sendCart,
    sendEcommerceConfig,
    sendObtienePedidos,
    sendOrderState,
    sendWebhook,
    setClearCart,
    setIDPedido,
    successCancelaPedidos,
    successDetailOrder,
    successEcommerceConfig,
    successGetProducts,
    successObtienePedidos,
    successProductPromo,
    successPromosEcommerce,
    successSendCart,
    successSendOrder,
    successSendWebhook
} from '../actions';

/**
 * Obtiene los productos disponibles del usuario
 */
export interface ProductsListState {
    error: Error | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    productsList: IProductos[];
    productosSugeridos: IProductos[];
    gamificacion: IProductos[];
    cuentasClave: IProductos[];
    filtros: IGroup[];
    recomendadores: IRecommender[];
    categorias: ICategories[];
    agrupadores: IGroupers[];
}

export const ProductsListInitState: ProductsListState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    productsList: [],
    productosSugeridos: [],
    gamificacion: [],
    cuentasClave: [],
    filtros: [],
    recomendadores: DEFAULT_RECOMMENDED,
    categorias: [],
    agrupadores: [],
};

export const productsReducer = createReducer(ProductsListInitState,
    on(getProducts, state => (
        {
            ...state,
            loading: true,
            success: false,
            failed: false
        }
    )),
    on(successGetProducts, (state, resp) => (
        {
            ...state,
            loading: false,
            success: true,
            productsList: resp?.data?.[0] || [],
            filtros: resp?.data?.[1] || [],
            productosSugeridos: resp?.data?.[2] || [],
            gamificacion: resp?.data?.[3] || [],
            recomendadores: resp?.data?.[4] || [],
            categorias: resp?.data?.[5] || [],
            agrupadores: resp?.data?.[6] || [],
            cuentasClave: resp?.data?.[7] || [],
        }
    )),
    on(errorGetProducts, (state, { payload }) => (
        {
            ...state,
            loading: false,
            failed: true,
            error: { ...payload }
        }
    )),
    on(resetProducts, state => ({
        ...state
        , ...ProductsListInitState
    })),
    on(addProductsCartState, (state, { cartProducto }) => (
        {
            ...state,
            productsList: state.productsList.map(product => {
                const index = cartProducto && cartProducto.length > 0 ? cartProducto.findIndex(x => x.id_producto === product.id_producto) : -1
                if (index >= 0) {
                    return {
                        ...product,
                        productos: cartProducto[index].productos
                    };
                } else {
                    return product;
                }
            })
        }
    )),
    on(addProductCartState, (state, { id_producto, productSelect }) => (
        {
            ...state,
            productsList: state.productsList.map(product => {
                if (id_producto === product.id_producto) {
                    return {
                        ...product,
                        productos: productSelect
                    }
                } else {
                    return product
                }
            })
        }
    )),
);

/**
 * Cart State
 */
export interface CartProductsState {
    pedidoActual?: PedidoActualEntity;
    cartProductos: Array<IProductos>;
    cartPromos: Array<IProductos>;
    promo: Array<IPromosEcommerce>;
    clearCart?: boolean;
    updateCart?: boolean;
    summaryPrice?: ISummaryPrice;
    fecha_entrega?: string;
}

export const CartProductsInitState: CartProductsState = {
    cartProductos: [],
    cartPromos: [],
    promo: [],
    pedidoActual: { numero_pedido: '', limit_date: '' } as PedidoActualEntity,
    clearCart: true,
    updateCart: false,
    summaryPrice: SummaryPriceDefault,
    fecha_entrega: '',
};

/**
 * Agrega o actualiza productos al carrito
 * @param cartProductos state de productos actual
 * @param cartProducto producto a agregar/actualizar
 * @param productsSelect seleccion de cuantos agregar/actualizar
 * @returns nuevo array de productos con el producto seleccionado
 */
const pushUpdateCart = (cartProductos: Array<IProductos>, cartProducto: IProductos, productsSelect: number) => {
    const index = cartProductos && cartProductos.length > 0 ? cartProductos.findIndex(x => x.id_producto === cartProducto.id_producto && x.isCarrito) : -1
    if (index >= 0) {
        return cartProductos.map(product => {
            if (cartProducto.id_producto === product.id_producto && product.isCarrito) {
                return {
                    ...product,
                    productos: productsSelect,
                    modelo: cartProducto.modelo === null ? product.modelo : cartProducto.modelo
                }
            } else {
                return product
            }
        }).filter(p => p.productos !== 0)
    } else {
        return cartProductos.concat([
            {
                ...cartProducto,
                productos: productsSelect,
                isCarrito: true,
            }
        ]).filter(p => p.productos !== 0)
    }
}

/**
 * Agrega o actualiza productos de la promo al carrito
 * @param promo promocion que se ira agregar/actualizar
 * @param currentCartPromos state de productos actual
 * @param newCartPromos productos a agregar/actualizar
 * @param productsSelect seleccion de cuantas promos agregar/actualizar * cantidad de productos max
 * @returns nuevo array de productos de la promo seleccionada
 */
const pushUpdateCartPromo = (promo: IPromosEcommerce, currentCartPromos: Array<IProductos>, newCartPromos: Array<IProductos>, productsSelect: number) => {
    newCartPromos = newCartPromos.filter(ncp => (ncp.productos || 0) > 0);
    if (currentCartPromos.length < 1) {
        return newCartPromos.map(product => ({
            ...product,
            productos: (product.productos || 0) * productsSelect,
            isCarrito: true,
        }));
    } else {
        // Filtra todos los producto que no son de la promo
        const filterAllCurrent = currentCartPromos.filter(product => product.ClavePromo !== promo.ClavePromo);
        // Agregada multiplicador de productos*promoseleccion, y que son carrito
        newCartPromos = newCartPromos.map(product => ({ ...product, productos: (product.productos || 0) * productsSelect, isCarrito: true }));
        // Filtra los productos de la promo de orden actual
        const filterCurrent = currentCartPromos.filter(product => product.ClavePromo === promo.ClavePromo && !product.isCarrito);
        if (filterCurrent.length > 0) {
            // Valida si esta resta productos, y pone carrito
            const edited = filterCurrent.map(fc => {
                const index = newCartPromos.findIndex(ncp => fc.clave_producto === ncp.clave_producto && fc.Nivel === ncp.Nivel);
                return index >= 0
                    ? {
                        ...fc,
                        productos: fc.productos !== newCartPromos[index].productos ? newCartPromos[index].productos : fc.productos,
                        isCarrito: fc.productos !== newCartPromos[index].productos,
                    }
                    : { ...fc, productos: -(fc.productos || 0), isCarrito: true };
            });
            edited.forEach(prd => {
                const index = newCartPromos.findIndex(ncp => prd.clave_producto === ncp.clave_producto && prd.Nivel === ncp.Nivel);
                if (index >= 0 && !prd.isCarrito) {
                    newCartPromos.splice(index, 1);
                } else if (index >= 0) {
                    newCartPromos.splice(index, 1);
                }
            });
            newCartPromos = newCartPromos.concat(edited.filter(fc => fc.isCarrito));
        }
        const prdtsAtlPromos = currentCartPromos.filter(product => product.ClavePromo === promo.ClavePromo && !product.isCarrito);
        return filterAllCurrent.concat(newCartPromos, prdtsAtlPromos);
    }
}

/**
 * Agrega o actualiza promociones al carrito
 * @param cartPromo  state de promociones actual
 * @param promo  promocion a agregar/actualizar
 * @param promoSelect seleccion de cuantas promos agregar/actualizar
 * @returns nuevo array de promociones con su selección
 */
const pushUpdatePromoCart = (cartPromo: Array<IPromosEcommerce>, promo: IPromosEcommerce, promoSelect: number) => {
    const index = cartPromo.length > 0 ? cartPromo.findIndex(x => x.ClavePromo === promo.ClavePromo && x.isCarrito) : -1;
    if (index >= 0) {
        return cartPromo.map(pr => {
            if (promo.ClavePromo === pr.ClavePromo && pr.isCarrito) {
                return {
                    ...pr,
                    PaquetesCanjeados: promoSelect
                };
            } else {
                return pr;
            }
        })
    } else {
        return cartPromo.concat([
            {
                ...promo,
                PaquetesCanjeados: promoSelect,
                isCarrito: true,
            }
        ]);
    }
}

/**
 * Remueve del carrito el producto, pero valida si es de un pedido realizado o es de carrito inato
 * @param idproducto clave del producto a eliminar
 * @param stateProductos  state de productos actual
 * @returns nuevo array de productos, con negativo o sin producto por eliminar
 */
const removePrdCartStt = (idproducto: number, stateProductos: IProductos[]) => {
    const index = stateProductos.findIndex(x => x.id_producto === idproducto && x.isCarrito);
    let prdActual = stateProductos.filter(p => idproducto === p.id_producto && !p.isCarrito);
    prdActual = prdActual.length > 0 ? prdActual : new Array({ ...stateProductos[index], productos: 0 });
    const removeCart: IProductos = { ...prdActual[0], productos: -(prdActual[0].productos || 0), isCarrito: true };
    if (index >= 0) {
        return stateProductos.map((sp, ix) => ix === index ? removeCart : sp).filter(p => p.productos !== 0);
    } else {
        return stateProductos.concat(removeCart).filter(p => p.productos !== 0);
    }
}

/**
 * Remueve del carrito la promocion, pero valida si es de un pedido realizado o es de carrito inato
 * @param ClavePromo clave de la promo a eliminar
 * @param statePromo  state de promociones actual
 * @param statePProductos  state de productos de promociones actual
 * @returns nuevo array de promociones y sus productos, con negativo o sin producto por eliminar
 */
const removePmCartStt = (ClavePromo: string, statePromo: IPromosEcommerce[], statePProductos: IProductos[]) => {
    // Remover Promos
    const index = statePromo.findIndex(x => x.ClavePromo === ClavePromo && x.isCarrito);
    let pmActual = statePromo.filter(p => p.ClavePromo === ClavePromo && !p.isCarrito);
    pmActual = pmActual.length > 0 ? pmActual : new Array({ ...statePromo[index], PaquetesCanjeados: 0 });
    const removeCart: IPromosEcommerce = { ...pmActual[0], PaquetesCanjeados: -(pmActual[0].PaquetesCanjeados), isCarrito: true };
    let promo: IPromosEcommerce[] = [];
    if (index >= 0) {
        promo = statePromo.map((sp, ix) => ix === index ? removeCart : sp).filter(p => p.PaquetesCanjeados !== 0);
    } else {
        promo = statePromo.concat(removeCart).filter(p => p.PaquetesCanjeados !== 0);
    }
    // Remover productos promo
    let prdsPmDff = statePProductos.filter(p => p.ClavePromo !== ClavePromo);
    const prdsPmCrF = statePProductos.filter(p => p.ClavePromo === ClavePromo && !p.isCarrito);

    const prdsPmCrT = prdsPmCrF.map(p => ({ ...p, productos: -(p.productos || 0), isCarrito: true }));
    prdsPmDff = prdsPmDff.concat(prdsPmCrF, prdsPmCrT);

    return { promo, cartPromos: prdsPmDff };
}

export const cartProductsReducer = createReducer(CartProductsInitState,
    on(addCartState, (state, { cartProducto, productsSelect }) => (
        {
            ...state,
            cartProductos: pushUpdateCart(state.cartProductos, cartProducto, productsSelect),
            clearCart: true,
            updateCart: true,
        }
    )),
    on(addingProductsToCartState, (state, cart) => (
        {
            ...state,
            cartProductos: state.cartProductos.concat(cart.cartProductos),
            cartPromos: state.cartPromos.concat(cart.cartPromos),
            promo: state.promo.concat(cart.promo),
            updateCart: false,
        }
    )),
    on(clearCartProducts, state => ({
        ...state
        , ...CartProductsInitState
        , clearCart: true
        , updateCart: false
    })),
    on(removeCartState, (state, { id_producto }) => (
        {
            ...state,
            cartProductos: removePrdCartStt(id_producto, state.cartProductos),
            clearCart: true,
            updateCart: true,
        }
    )),
    // Promos State
    on(addingPromosToCartState, (state, { cartPromos }) => (
        {
            ...state,
            cartPromos
        }
    )),
    on(addCartPromoState, (state, { promo, cartPromos, promoSelect }) => (
        {
            ...state,
            promo: pushUpdatePromoCart(state.promo, promo, promoSelect),
            cartPromos: pushUpdateCartPromo(promo, state.cartPromos, cartPromos, promoSelect),
            clearCart: true,
            updateCart: true,
        }
    )),
    on(removeCartPromoState, (state, { ClavePromo }) => (
        {
            ...state,
            ...removePmCartStt(ClavePromo, state.promo, state.cartPromos),
            clearCart: true,
            updateCart: true,
        }
    )),
    // ID_Pedido
    on(setIDPedido, (state, { pedidoActual, fecha_entrega }) => ({ ...state, pedidoActual, fecha_entrega })),
    // Set clearCart, si debe o no limpiar el state
    on(setClearCart, (state, { clearCart }) => ({ ...state, clearCart, updateCart: false })),
);


/**
 * Promociones ecommerce
 */

export interface PromosEcommerceState {
    error: Error | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    PromoModulo: boolean;
    promosEcommerce: IPromosEcommerce[];
    editPromo: number;
    ClavePromo: string;
}

export const PromosEcInitState: PromosEcommerceState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    PromoModulo: false,
    promosEcommerce: [],
    editPromo: 0,
    ClavePromo: ''
};

export const promosEcommerceReducer = createReducer(PromosEcInitState,
    on(getPromosEcommerce, (state) => (
        {
            ...state,
            loading: true,
            success: false,
            failed: false,
        }
    )),
    on(successPromosEcommerce, (state, resp) => (
        {
            ...state,
            loading: false,
            success: true,
            promosEcommerce: [...resp.data],
            editPromo: state.ClavePromo.length > 0 ? resp.data.findIndex(promo => promo.ClavePromo === state.ClavePromo) + 1 : 0
        }
    )),
    on(errorPromosEcommerce, (state, { payload }) => (
        {
            ...state,
            loading: false,
            failed: true,
            error: { ...payload }
        }
    )),
    on(resetPromosEcommerce, state => ({
        ...state
        , ...PromosEcInitState
    })),
    on(editPromo, (state, { ClavePromo }) => ({
        ...state,
        ClavePromo,
        editPromo: ClavePromo ? state.promosEcommerce.findIndex(promo => promo.ClavePromo === ClavePromo) + 1 : 0
    })),
    on(resetEditPromo, (state) => ({ ...state, editPromo: 0, ClavePromo: '' })),
    on(activePromosEcommerce, (state, { PromoModulo }) => ({ ...state, PromoModulo })),
);


/**
 * Productos de promociones ecommerce
 */

export interface ProductsPromoState {
    error: Error | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    productPromo: IProductos[];
    credentials: ICallableParams;
}

export const ProductsPromoInitState: ProductsPromoState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    productPromo: [],
    credentials: {
        ClavePromo: '',
    }
};

export const productsPromoReducer = createReducer(ProductsPromoInitState,
    on(successProductPromo, (state, { data }) => (
        {
            ...state,
            loading: false,
            success: true,
            productPromo: state.productPromo.filter(p => p.ClavePromo !== state.credentials.ClavePromo).concat(data),
        }
    )),
);


/**
 * Guardado del Carrito
 */

export interface SendCartState {
    error: Error | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    ID_Carrito: number;
}

export const SendCartInit: SendCartState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    ID_Carrito: 0,
};

export const sendCartReducer = createReducer(SendCartInit,
    on(sendCart, state => (
        {
            ...state,
            loading: true,
            failed: false,
            success: false,
        }
    )),
    on(successSendCart, (state, { data }) => (
        {
            ...state,
            loading: false,
            success: true,
            ID_Carrito: data.ID_Carrito,
        }
    )),
    on(errorSendCart, (state, { payload }) => (
        {
            ...state,
            loading: false,
            success: false,
            failed: true,
            error: { ...payload }
        }
    )),
    on(resetSendCart, state => ({ ...state, ...SendCartInit })),
);


/**
 * Envio del pedido
 */

export interface PedidoState {
    error: any | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    respOrder: IRespOrder | null;
    credentials: ICredPedidoSta;
}

interface ICredPedidoSta {
    pedidos?: IProductos[];
    promos?: IPromosEcommerce[];
    clearCart: boolean;
    ID_Pedido?: number;
};

export const PedidosInitState: PedidoState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    credentials: { clearCart: false } as ICredPedidoSta,
    respOrder: null,
};

export const pedidoReducer = createReducer(PedidosInitState,
    on(sendOrderState, (state, { pedidos, promos }) => (
        {
            ...state,
            loading: true,
            failed: false,
            success: false,
            credentials: { pedidos, promos, clearCart: true },
        }
    )),
    on(successSendOrder, (state, resp) => (
        {
            ...state,
            loading: false,
            success: true,
            respOrder: { ...resp.data[0] }
        }
    )),
    on(errorSendOrder, (state, { payload }) => (
        {
            ...state,
            loading: false,
            success: false,
            failed: true,
            error: { ...payload }
        }
    )),
    on(detailOrder, (state, { endpoint, respOrder }) => (
        {
            ...state,
            loading: true,
            failed: false,
            success: false,
            credentials: { endpoint, ID_Pedido: respOrder.id_pedido, clearCart: false }
        }
    )),
    on(successDetailOrder, (state, { respOrder }) => (
        {
            ...state,
            loading: false,
            success: true,
            respOrder: { ...respOrder }
        }
    )),
    on(errorDetailOrder, (state, { payload }) => (
        {
            ...state,
            loading: false,
            success: false,
            failed: true,
            error: { ...payload }
        }
    )),
    on(resetSendOrder, state => ({
        ...state
        , ...PedidosInitState
    }))
);


/**
 * Obtiene Pedidos
 */

export interface ObtienePedidoState {
    error: Error | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    respPedidos: IDataCurrentOrders;
}

export const ObtienePedidosInitState: ObtienePedidoState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    respPedidos: {
        carrito_actual: [],
        pedido_actual: [],
    } as IDataCurrentOrders,
};

export const obtienePedidoReducer = createReducer(ObtienePedidosInitState,
    on(sendObtienePedidos, state => (
        {
            ...state,
            loading: true,
            failed: false,
            success: false
        }
    )),
    on(successObtienePedidos, (state, resp) => (
        {
            ...state,
            loading: false,
            success: true,
            respPedidos: { ...resp.data }
        }
    )),
    on(errorObtienePedidos, (state, { payload }) => (
        {
            ...state,
            loading: false,
            failed: true,
            success: false,
            error: { ...payload }
        }
    )),
    on(resetObtienePedidos, state => ({
        ...state,
        ...ObtienePedidosInitState
    }))
);


/**
 * Cancela Pedidos
 */

export interface CancelaPedidoState {
    error: Error | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    cancelPedido: IDataCancelaPedidos | null;
}

export const CancelPedidoInitState: CancelaPedidoState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    cancelPedido: null
};

export const cancelPedidoReducer = createReducer(CancelPedidoInitState,
    on(sendCancelaPedidos, state => (
        {
            ...state,
            loading: true,
            failed: false,
            success: false,
        }
    )),
    on(successCancelaPedidos, (state, resp) => (
        {
            ...state,
            loading: false,
            success: true,
            cancelPedido: { ...resp.data }
        }
    )),
    on(errorCancelaPedidos, (state, { payload }) => (
        {
            ...state,
            loading: false,
            success: false,
            failed: true,
            error: { ...payload }
        }
    )),
    on(resetCancelaPedidos, state => ({
        ...state
        , ...CancelPedidoInitState
    }))
);


/**
 * Resume detail order
 */
export const orderDetailReducer = createReducer(CartProductsInitState,
    on(addingProductsToResumeState, (state, { cartProductos, cartPromos, promo, pedidoActual, summaryPrice }) => (
        {
            ...state,
            cartProductos: cartProductos ? cartProductos : [],
            cartPromos: cartPromos ? cartPromos : [],
            promo: promo ? promo : [],
            pedidoActual,
            summaryPrice,
        }
    )),
    on(clearDetailOrder, state => ({ ...state, ...CartProductsInitState })),
);


/**
 * Manda informaión al Webhook
 */

export interface SendWebhookState {
    error: Error | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    data: IRespSendWebhook;
}

export const SendWebhookInit: SendWebhookState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    data: {
        error: false,
    } as IRespSendWebhook,
};

export const sendWebhookReducer = createReducer(SendWebhookInit,
    on(sendWebhook, state => (
        {
            ...state,
            loading: true,
            failed: false,
            success: false,
        }
    )),
    on(successSendWebhook, (state, { data }) => (
        {
            ...state,
            loading: false,
            success: true,
            data,
        }
    )),
    on(errorSendWebhook, (state, { payload }) => (
        {
            ...state,
            loading: false,
            success: false,
            failed: true,
            error: { ...payload }
        }
    )),
    on(resetSendWebhook, state => ({ ...state, ...SendWebhookInit })),
);


/**
 * Ecommerce Config
 */

export interface EcommerceConfigState {
    error: Error | null;
    failed: boolean;
    success: boolean;
    loading: boolean;
    respConfig: IDataEcommerceConfig;
}

export const EcommerceConfigInitState: EcommerceConfigState = {
    error: null,
    failed: false,
    success: false,
    loading: false,
    respConfig: {
        fecha_entrega_copy: '',
        frecuencia_entrega: '',
        ID_Programa: 0,
    } as IDataEcommerceConfig,
};

export const ecommerceConfigReducer = createReducer(EcommerceConfigInitState,
    on(sendEcommerceConfig, state => (
        {
            ...state,
            loading: true,
            failed: false,
            success: false
        }
    )),
    on(successEcommerceConfig, (state, resp) => (
        {
            ...state,
            loading: false,
            success: true,
            respConfig: { ...resp.data }
        }
    )),
    on(errorEcommerceConfig, (state, { payload }) => (
        {
            ...state,
            loading: false,
            failed: true,
            success: false,
            error: { ...payload }
        }
    )),
    on(resetEcommerceConfig, state => ({ ...state, ...EcommerceConfigInitState })),
);

